import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, AccessibilitySettings } from '../types';

interface ThemeContextType {
  theme: Theme;
  accessibility: AccessibilitySettings;
  setTheme: (theme: Theme) => void;
  setAccessibility: (settings: AccessibilitySettings) => void;
  toggleTheme: () => void;
  toggleViewMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const defaultTheme: Theme = {
  mode: 'system',
  viewMode: 'standard'
};

const defaultAccessibility: AccessibilitySettings = {
  highContrast: false,
  fontSize: 'normal',
  reducedMotion: false,
  colorBlindSupport: false,
  screenReaderOptimized: false
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [accessibility, setAccessibilityState] = useState<AccessibilitySettings>(defaultAccessibility);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      const savedAccessibility = localStorage.getItem('accessibility');
      
      if (savedTheme) {
        try {
          const parsed = JSON.parse(savedTheme);
          // Validate it's an object with expected structure
          if (parsed && typeof parsed === 'object' && 'mode' in parsed) {
            setThemeState(parsed);
          } else {
            // Invalid format, clear it
            localStorage.removeItem('theme');
          }
        } catch (e) {
          // Invalid JSON, clear it
          console.warn('Clearing invalid theme data from localStorage');
          localStorage.removeItem('theme');
        }
      }
      
      if (savedAccessibility) {
        try {
          const parsed = JSON.parse(savedAccessibility);
          if (parsed && typeof parsed === 'object') {
            setAccessibilityState(parsed);
          } else {
            localStorage.removeItem('accessibility');
          }
        } catch (e) {
          console.warn('Clearing invalid accessibility data from localStorage');
          localStorage.removeItem('accessibility');
        }
      }
    } catch (error) {
      console.warn('Failed to load theme settings from localStorage:', error);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply theme mode
    if (theme.mode === 'dark' || (theme.mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Apply accessibility settings
    root.setAttribute('data-font-size', accessibility.fontSize);
    root.setAttribute('data-high-contrast', accessibility.highContrast.toString());
    root.setAttribute('data-reduced-motion', accessibility.reducedMotion.toString());
    root.setAttribute('data-color-blind', accessibility.colorBlindSupport.toString());
    root.setAttribute('data-screen-reader', accessibility.screenReaderOptimized.toString());
    
    // Apply view mode
    root.setAttribute('data-view-mode', theme.viewMode);
    
  }, [theme, accessibility]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', JSON.stringify(newTheme));
  };

  const setAccessibility = (newSettings: AccessibilitySettings) => {
    setAccessibilityState(newSettings);
    localStorage.setItem('accessibility', JSON.stringify(newSettings));
  };

  const toggleTheme = () => {
    const newMode = theme.mode === 'light' ? 'dark' : theme.mode === 'dark' ? 'system' : 'light';
    setTheme({ ...theme, mode: newMode });
  };

  const toggleViewMode = () => {
    setTheme({ ...theme, viewMode: theme.viewMode === 'standard' ? 'compact' : 'standard' });
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      accessibility,
      setTheme,
      setAccessibility,
      toggleTheme,
      toggleViewMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
